/**
 * Matching candidat ↔ référentiel d'une cible, pour toute la campagne.
 *
 * Partagé par l'onglet Matching de la campagne (classement + restitution du
 * candidat sélectionné) et par la fiche candidat (restitution d'un seul
 * candidat, avec son rang). Les deux DOIVENT lire le même moteur contre la même
 * version : un score qui différerait d'un écran à l'autre ne vaudrait rien.
 *
 * Le score n'est JAMAIS stocké : il est recalculé à la lecture contre une
 * projection du référentiel (plan §8.1). C'est ce qui garantit que tous les
 * candidats de la campagne sont comparés au même référentiel — et que le
 * classement est légitime (§4.1).
 */
import { useMemo } from "react";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { extractScoresFromResult, type TraitResult } from "@repo/results";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import type {
  StatDimension,
  StatFourchette,
} from "../../../../convex/referentiel/agregation";
import {
  niveauxForcesQsort,
  seuilReferentsFiable,
} from "../../../../convex/referentiel/agregation";
import type { ResultatComparaison } from "../../../../convex/referentiel/comparer";
import {
  comparerFourchette,
  comparerQsort,
} from "../../../../convex/referentiel/comparer";
import { scoreDuNiveau } from "../../../../convex/referentiel/qsort";

/** Une évaluation PAMI de la campagne, telle que renvoyée par
 *  `obtenirEvaluationsPamiParCampagne`. */
export type EvaluationPamiRow = {
  _id: Id<"evaluations">;
  parcoursId: Id<"parcoursEvaluations">;
  nomCandidat?: string;
  prenomCandidat?: string;
  scoresJson?: string;
};

/** Une passation fit-culturel, telle que renvoyée par
 *  `obtenirEvaluationsFitCulturelParCampagne`. Le NIVEAU est brut : le score est
 *  recalculé ici depuis la pyramide de la campagne (§1.3). */
export type EvaluationFitCulturelRow = {
  _id: Id<"evaluations">;
  parcoursId: Id<"parcoursEvaluations">;
  nomCandidat?: string;
  prenomCandidat?: string;
  dateFin?: number;
  niveaux: { code: string; niveau: number }[];
};

export type EntreeMatching = {
  row: EvaluationPamiRow | EvaluationFitCulturelRow;
  comparaison: ResultatComparaison | null;
  /** Q-sort seulement : conservé pour bâtir le rapport culturel du candidat. */
  vecteurCandidat?: Record<string, number>;
};

type EtatReferentiel = NonNullable<
  FunctionReturnType<typeof api.referentiel.campagne.obtenirEtatReferentiel>
>;
type EchelleQsort = Extract<
  EtatReferentiel["config"]["echelle"],
  { kind: "qsort" }
>;
export type VersionReferentiel = EtatReferentiel["historique"][number];

export type MatchingReferentiel = {
  /** `undefined` = chargement, `null` = pas de référentiel sur la campagne. */
  etat: EtatReferentiel | null | undefined;
  versions: VersionReferentiel[];
  /** Version comparée. Absente tant qu'aucun référent n'a contribué. */
  version: VersionReferentiel | undefined;
  /** Fourchette (PAMI) : bornes de l'échelle. `null` en Q-sort. */
  echelle: { min: number; max: number } | null;
  /** Q-sort (fit culturel) : pyramide de la campagne. `null` en fourchette. */
  pyramide: EchelleQsort["pyramide"] | null;
  niveauxForces: ReturnType<typeof niveauxForcesQsort> | undefined;
  /** Trié par score décroissant, les candidats sans score en fin de liste. */
  classement: EntreeMatching[];
  /** Référentiel ou passations encore en chargement. */
  chargement: boolean;
};

/**
 * Calcule le classement d'une cible. `actif: false` n'interroge rien et rend un
 * matching vide en chargement — pour les écrans qui n'affichent pas cette cible.
 *
 * `versionChoisie` absente = la DERNIÈRE version : c'est la seule contre
 * laquelle tous les candidats sont comparables entre eux.
 */
export function useMatchingReferentiel({
  campagneId,
  cible,
  evaluationsPami,
  evaluationsFitCulturel,
  versionChoisie,
  actif = true,
}: {
  campagneId: Id<"campagnes">;
  cible: "pami" | "pc-fit";
  evaluationsPami: EvaluationPamiRow[] | undefined;
  evaluationsFitCulturel: EvaluationFitCulturelRow[] | undefined;
  versionChoisie?: number | null;
  actif?: boolean;
}): MatchingReferentiel {
  const etat = useQuery(
    api.referentiel.campagne.obtenirEtatReferentiel,
    actif ? { campagneId, cible } : "skip",
  );

  const versions = useMemo(() => etat?.historique ?? [], [etat]);
  const version =
    versions.find((v) => v.version === versionChoisie) ??
    versions[versions.length - 1];

  // Mémoïsés : identité stable, sinon le classement serait recalculé à chaque
  // rendu (le moteur est pur, mais la comparaison de dépendances ne l'est pas).
  const echelleConfig = etat?.config.echelle;
  const echelle = useMemo(
    () =>
      echelleConfig?.kind === "fourchette"
        ? { min: echelleConfig.min, max: echelleConfig.max }
        : null,
    [echelleConfig],
  );
  const pyramide = useMemo(
    () => (echelleConfig?.kind === "qsort" ? echelleConfig.pyramide : null),
    [echelleConfig],
  );

  /**
   * Reclassement forcé de la version comparée — la structure de la pyramide du
   * référentiel, qui doit être celle du candidat (§5.1). Portée par la
   * projection ; recalculée pour les versions figées avant son introduction (même
   * fonction pure, donc mêmes niveaux).
   *
   * Une version antérieure garde SON reclassement : c'est ce qui rend un score
   * relu contre v2 identique à ce qu'il était sous v2.
   */
  const niveauxForces = useMemo(() => {
    if (!etat || !version || !pyramide) return undefined;
    return (
      version.niveauxForces ??
      niveauxForcesQsort({
        dimensions: etat.config.dimensions,
        agregat: version.dimensions as Record<string, StatDimension>,
        pyramide,
      })
    );
  }, [etat, version, pyramide]);

  /**
   * Un classement, quelle que soit l'échelle. Les deux branches produisent la
   * MÊME forme (§5.4), donc tout ce qui suit ne connaît qu'une seule structure.
   */
  const classement = useMemo(() => {
    if (!etat || !version) return [];

    const lignes: EntreeMatching[] = [];

    if (echelle && evaluationsPami) {
      const agregat = version.dimensions as Record<string, StatFourchette>;
      for (const row of evaluationsPami) {
        const scoresCandidat = row.scoresJson
          ? extractScoresFromResult(JSON.parse(row.scoresJson) as TraitResult[])
          : null;
        lignes.push({
          row,
          comparaison: scoresCandidat
            ? comparerFourchette({
                dimensions: etat.config.dimensions,
                scoresCandidat,
                agregat,
                echelle,
                nbReferents: version.nbReferents,
                codesDesaccord: version.dimensionsEnDesaccord,
                // PAMI : référent unique, le seuil de 3 marquerait toujours
                // `faible`.
                seuilReferents: seuilReferentsFiable(etat.config.cible),
              })
            : null,
        });
      }
    }

    if (pyramide && evaluationsFitCulturel) {
      const agregat = version.dimensions as Record<string, StatDimension>;
      for (const row of evaluationsFitCulturel) {
        // Le score est RECALCULÉ depuis le niveau et la pyramide de la campagne :
        // le `score` stocké dans la réponse n'est qu'un confort de relecture.
        const vecteurCandidat = Object.fromEntries(
          row.niveaux.map(({ code, niveau }) => [
            code,
            scoreDuNiveau(niveau, pyramide.length),
          ]),
        );
        lignes.push({
          row,
          vecteurCandidat,
          comparaison: comparerQsort({
            dimensions: etat.config.dimensions,
            vecteurCandidat,
            agregat,
            pyramide,
            nbReferents: version.nbReferents,
            niveauxForces,
          }),
        });
      }
    }

    // Sans score en fin de liste. Pas de valeur plancher en repli : en Q-sort
    // le score est r, et −1 y est un résultat, pas une absence.
    return lignes.sort((a, b) => {
      const sa = a.comparaison?.scoreMatching ?? null;
      const sb = b.comparaison?.scoreMatching ?? null;
      if (sa === null || sb === null) {
        return (sa === null ? 1 : 0) - (sb === null ? 1 : 0);
      }
      return sb - sa;
    });
  }, [
    etat,
    echelle,
    pyramide,
    niveauxForces,
    version,
    evaluationsPami,
    evaluationsFitCulturel,
  ]);

  const chargement =
    etat === undefined ||
    (echelle !== null && evaluationsPami === undefined) ||
    (pyramide !== null && evaluationsFitCulturel === undefined);

  return {
    etat,
    versions,
    version,
    echelle,
    pyramide,
    niveauxForces,
    classement,
    chargement,
  };
}
